import { Plugin } from '@remirror/pm/state';
import { includes, isElementDomNode, omitExtraAttributes } from 'remirror';
import { LinkExtension } from 'remirror/extensions';
import clientService from '../../../cms/src/apiClient';

const documentUri =
  'data:image/webp;base64,UklGRugAAABXRUJQVlA4INwAAAAQBgCdASolACUAPpFGnkulo6Kho4gAsBIJaQAD5qPSTonuvxm+a3ulvfc92GY/Y1tyHEPmmOCWW/QA/v7Dn6a7XUGeGRFwek/c7HzuZqWHSF8PPQPTX9vHkXsEicp67SaxkvpzvfI7B+yj2xVWUEdu87Q6hfI459ps0z23lm3xiSgui5AOoeVpDdt5fcHgEb9CEfhCxOyAiFNWXHdmgECiKG15AcQIiMRmDKxyxftgkbf8g3tSKWtagTUTinWy0PC0tuIHs3LhjVdcDEjkUFcm6UT7J4gwnU1FAAAA';
const workspaceUri =
  'data:image/webp;base64,UklGRi4BAABXRUJQVlA4ICIBAAAwBgCdASolACUAPoU2lkelIyIhLi5goBCJaQAV4BNVql3Yh785k9+ocUYEa19w8uA1A/T2+i5lJ8gAAP7sEjuH2ro1VZnVrtUM1xG26b8ZrfPeGVFdQdospOWOPU3JwIcaZbTOPAdj7Q2rufqSzDGb7i1+8QV/f7pgSXJL9w4TFsYYr9kcniDyrUbJw+OEJ5J+XZC/8J+uKP3OmuylO66OIel7maXvB/RnsZOJHJRPX/0ItZTeaUACf6TiVj+O+jACN//aafbrG9omHIiC/V+F+B0md5XHMtVl8SDUughYyDecUYAoqtGa2o0v/rEGubsD+H4Yme688MT+C0astcGv2c9V1VdauhlzNT9uKH7/oQCm6Blh+K4EUhcpyEV3jAAAAA==';

function getLinkType(href) {
  const documentPattern = /^\/spaces\/[0-9a-fA-F-]{36}\/pages(\/[0-9a-fA-F-]{36})+$/;
  const workspacePattern = /^\/spaces\/[0-9a-fA-F-]{36}$/;
  if (documentPattern.test(href)) return 'document';
  if (workspacePattern.test(href)) return 'workspace';
  return 'url';
}

export class CustomLinkExtension extends LinkExtension {
  createExternalPlugins() {
    let popoverElement = null;
    let popoverTimeout = null;
    let abortController = null;

    const ogDataCache = new Map();
    let currentlyFetchingUrl = null;

    const fetchOgData = async (url) => {
      if (ogDataCache.has(url)) {
        return ogDataCache.get(url);
      }

      if (url === currentlyFetchingUrl) {
        return null;
      }

      if (abortController) {
        abortController.abort();
      }

      abortController = new AbortController();
      currentlyFetchingUrl = url;

      try {
        const ogData = await clientService.get(`util/fetch-og?url=${url}`, { signal: abortController.signal });

        if (ogData) {
          ogDataCache.set(url, ogData);
        }
        currentlyFetchingUrl = null;

        return ogData;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching OG data:', error);
        }
        currentlyFetchingUrl = null;
        return null;
      }
    };

    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            mouseover: async (view, event) => {
              if (!popoverElement) return;

              const target = event.target.closest('.hyperlink');
              if (!target) return;

              const rect = target.getBoundingClientRect();
              popoverElement.style.top = `${rect.bottom + 4 + window.scrollY}px`;
              popoverElement.style.left = `${rect.left + window.scrollX}px`;

              clearTimeout(popoverTimeout);

              popoverTimeout = setTimeout(async () => {
                popoverElement.classList.add('hover-card-show');
                const href = target.querySelector('a').getAttribute('href');

                const linkType = getLinkType(href);
                if (linkType === 'document' || linkType === 'workspace') {
                  popoverElement.classList.remove('hover-card-show');
                  popoverElement.innerHTML = '';
                  return;
                }

                if (!ogDataCache.has(href)) {
                  popoverElement.innerHTML = '<div class="hover-card-loading"></div>';
                }

                const ogData = await fetchOgData(href);
                if (!ogData) {
                  popoverElement.classList.remove('hover-card-show');
                  return;
                }

                popoverElement.innerHTML = '';

                // textual og data
                const ogTextualWrapper = document.createElement('a');
                ogTextualWrapper.classList.add('hover-card-text-link');
                ogTextualWrapper.href = ogData['url'];
                ogTextualWrapper.target = '_blank';

                const ogTitleNode = document.createElement('p');
                ogTitleNode.classList.add('hover-card-title');
                if (ogData['og:title']) {
                  ogTitleNode.textContent = ogData['og:title'];
                } else {
                  ogTitleNode.textContent = ogData['title'];
                }
                ogTextualWrapper.appendChild(ogTitleNode);

                if (ogData['og:description']) {
                  const ogDescriptionNode = document.createElement('span');
                  ogDescriptionNode.textContent = ogData['og:description'];
                  ogDescriptionNode.classList.add('hover-card-description');
                  ogTextualWrapper.appendChild(ogDescriptionNode);
                }
                popoverElement.appendChild(ogTextualWrapper);

                // media og data
                if (ogData['og:image']) {
                  const ogImageWrapper = document.createElement('div');
                  const ogImageNode = document.createElement('img');
                  ogImageWrapper.classList.add('hover-card-image');
                  ogImageNode.src = ogData['og:image'];
                  ogImageNode.alt = ogData['og:title'];

                  ogImageWrapper.appendChild(ogImageNode);
                  popoverElement.appendChild(ogImageWrapper);
                }
              }, 300);
            },
            mouseout: (view, event) => {
              clearTimeout(popoverTimeout);

              if (abortController) {
                abortController.abort();
                abortController = null;
              }

              const relatedTarget = event.relatedTarget;

              if (
                relatedTarget !== popoverElement &&
                !popoverElement.contains(relatedTarget) &&
                !event.target.closest('.hyperlink')
              ) {
                popoverElement.classList.remove('hover-card-show');
                popoverElement.innerHTML = '';
              }
            },
          },
        },
        view: () => {
          popoverElement = document.createElement('div');
          popoverElement.classList.add('hyperlink-hover-card');
          popoverElement.addEventListener('mouseleave', () => popoverElement.classList.remove('hover-card-show'));
          document.body.appendChild(popoverElement);

          return {
            destroy: () => {
              popoverElement.remove();
              popoverElement = null;
              popoverTimeout = false;
              abortController = null;
              ogDataCache.clear();
            },
          };
        },
      }),
    ];
  }
  createMarkSpec(extra, override) {
    const AUTO_ATTRIBUTE = 'data-link-auto';

    function getDomain(href) {
      try {
        const url = new URL(href.startsWith('http') ? href : `https://${href}`);
        return url.hostname;
      } catch (error) {
        console.error('Invalid URL:', href, error);
        return null;
      }
    }

    const getTargetObject = (target) => {
      const { defaultTarget, supportedTargets } = this.options;
      const targets = defaultTarget ? [...supportedTargets, defaultTarget] : supportedTargets;
      return target && includes(targets, target) ? { target } : undefined;
    };

    return {
      inclusive: false,
      excludes: '_',
      ...override,
      attrs: {
        ...extra.defaults(),
        href: {},
        target: { default: this.options.defaultTarget },
        auto: { default: false },
      },
      parseDOM: [
        {
          tag: 'a[href]',
          getAttrs: (node) => {
            if (!isElementDomNode(node)) {
              return false;
            }

            const href = node.getAttribute('href');
            const text = node.textContent;

            // If link text content equals href value we "auto link"
            // e.g [test](//test.com) - not "auto link"
            // e.g [test.com](//test.com) - "auto link"
            const auto =
              this.options.autoLink &&
              (node.hasAttribute(AUTO_ATTRIBUTE) ||
                href === text ||
                href?.replace(`${this.options.defaultProtocol}//`, '') === text);

            return {
              ...extra.parse(node),
              href,
              auto,
              ...getTargetObject(node.getAttribute('target')),
            };
          },
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (node) => {
        const { auto: _, target: __, ...rest } = omitExtraAttributes(node.attrs, extra);
        const auto = node.attrs.auto ? { [AUTO_ATTRIBUTE]: '' } : {};
        const rel = 'noopener noreferrer nofollow';
        const href = node.attrs.href;
        const domain = getDomain(href);

        const documentIcon = [
          'img',
          {
            src: documentUri,
            alt: 'document icon',
            style: 'width:1em;height:1em;',
          },
        ];

        const workspaceIcon = [
          'img',
          {
            src: workspaceUri,
            alt: 'Workspace icon',
            style: 'width:1em;height:1em;',
          },
        ];

        const attrs = {
          ...extra.dom(node),
          ...rest,
          rel,
          title: href,
          ...auto,
          ...getTargetObject(node.attrs.target),
        };

        const anchor = ['a', attrs, 0];

        const linkType = getLinkType(href);

        if (linkType === 'document') {
          return ['span', { class: 'hyperlink' }, documentIcon, anchor];
        }

        if (linkType === 'workspace') {
          return ['span', { class: 'hyperlink' }, workspaceIcon, anchor];
        }

        if (linkType === 'url' && domain) {
          const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}`;
          return [
            'span',
            { class: 'hyperlink' },
            [
              'img',
              {
                src: faviconUrl,
                alt: `${domain} favicon`,
                style: 'margin-right:4px;vertical-align:middle;',
              },
            ],
            anchor,
          ];
        }

        return anchor;
      },
    };
  }
}
